/**
 * API Service Layer for KiddieScraping Phase 3 Frontend
 * Interfaces with the Phase 2 Analysis Engine REST API
 */

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL || 'http://localhost:8010'}/api`;

class ApiError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

// Request deduplication map to prevent concurrent identical requests
const ongoingRequests = new Map();

/**
 * Generic API call wrapper with error handling and request deduplication
 */
const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE}${endpoint}`;
    const method = options.method || 'GET';
    const requestKey = `${method}:${url}:${JSON.stringify(options.body || '')}`;

    // Check if this exact request is already in progress
    if (ongoingRequests.has(requestKey)) {
        console.log('🔄 Deduplicating concurrent request to:', url);
        return ongoingRequests.get(requestKey);
    }

    console.log('🔗 apiCall to:', url);

    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        ...options
    };

    // Create the request promise
    const requestPromise = (async () => {
        try {
            console.log('📤 Making fetch request...');
            const response = await fetch(url, defaultOptions);
            console.log('📨 Response status:', response.status, response.statusText);

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = { error: 'Network error' };
                }

                console.error('❌ API Error:', errorData);
                throw new ApiError(
                    errorData.error || errorData.detail || `HTTP ${response.status}`,
                    response.status,
                    errorData
                );
            }

            const jsonResponse = await response.json();
            console.log('✅ API Success:', jsonResponse);
            return jsonResponse;
        } catch (error) {
            console.error('💥 Network/Parse Error:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(`Network error: ${error.message}`, 0, null);
        } finally {
            // Always clean up the ongoing request
            ongoingRequests.delete(requestKey);
        }
    })();

    // Store the promise to deduplicate concurrent requests
    ongoingRequests.set(requestKey, requestPromise);

    return requestPromise;
};

/**
 * System & Health API
 */
export const systemApi = {
    // Health check
    healthCheck: () => fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8010'}/health`).then(r => r.json()),

    // System status with metrics
    getSystemStatus: () => apiCall('/system/status'),

    // Get available analysis types
    getAnalysisTypes: () => apiCall('/analysis/types'),

    // Get running analyses status
    getAnalysisStatus: (facilityId = null) =>
        apiCall(`/analysis/status${facilityId ? `?facility_id=${facilityId}` : ''}`),
};

export const facilitiesApi = {
    // Get all facilities
    getFacilities: () => apiCall('/facilities'),

    // Get a single facility with all details
    getFacility: (facilityId) => apiCall(`/facilities/${facilityId}`),

    // Get facilities with full status (more detailed)
    getFacilitiesWithStatus: () => apiCall('/facilities/with-status'),

    // Scrape facilities
    scrapeFacilities: (facilityIds, maxConcurrent, forceRescrape) =>
        apiCall('/facilities/scrape', {
            method: 'POST',
            body: JSON.stringify({
                facility_ids: facilityIds,
                max_concurrent: maxConcurrent,
                force_rescrape: forceRescrape
            })
        }),

    // Send to Kiddie API
    sendToKiddie: (facilityIds, includePricing, maxConcurrent) =>
        apiCall('/facilities/send-to-kiddie', {
            method: 'POST',
            body: JSON.stringify({
                facility_ids: facilityIds,
                include_pricing: includePricing,
                max_concurrent: maxConcurrent
            })
        }),

    // Send inspection reports to Kiddie API
    sendInspectionReportsToKiddie: (facilityIds, maxConcurrent) =>
        apiCall('/facilities/send-inspection-reports-to-kiddie', {
            method: 'POST',
            body: JSON.stringify({
                facility_ids: facilityIds,
                max_concurrent: maxConcurrent
            })
        }),

    // Refresh cache
    refreshCache: () => apiCall('/facilities/refresh-cache', { method: 'POST' }),
};

/**
 * Analysis API
 */
export const analysisApi = {
    // Run single analysis
    runAnalysis: (facilityId, analysisType, forceReanalysis = false, enableOptimization = null) =>
        apiCall(`/facilities/${facilityId}/analyze`, {
            method: 'POST',
            body: JSON.stringify({
                analysis_type: analysisType,
                force_reanalysis: forceReanalysis,
                enable_optimization: enableOptimization
            })
        }),

    // Run multiple analyses
    runMultipleAnalyses: (facilityId, analysisTypes, forceReanalysis = false, maxConcurrent = 3) =>
        apiCall(`/facilities/${facilityId}/analyze/multiple`, {
            method: 'POST',
            body: JSON.stringify({
                analysis_types: analysisTypes,
                force_reanalysis: forceReanalysis,
                max_concurrent: maxConcurrent
            })
        }),

    // Run full analysis (all types)
    runFullAnalysis: (facilityId, forceReanalysis = false) =>
        apiCall(`/facilities/${facilityId}/analyze/full?force_reanalysis=${forceReanalysis}`, {
            method: 'POST'
        }),

    // Batch analysis
    batchAnalysis: (facilityIds, analysisType, forceReanalysis = false, maxConcurrent = 5) =>
        apiCall('/analysis/batch', {
            method: 'POST',
            body: JSON.stringify({
                facility_ids: facilityIds,
                analysis_type: analysisType,
                force_reanalysis: forceReanalysis,
                max_concurrent: maxConcurrent
            })
        }),
};

/**
 * Results API
 */
export const resultsApi = {
    // Get all analysis results for a facility
    getFacilityResults: (facilityId) => apiCall(`/facilities/${facilityId}/results`),

    // Get specific analysis result
    getSpecificResult: (facilityId, analysisType) =>
        apiCall(`/facilities/${facilityId}/results/${analysisType}`),

    // Get analysis summaries for all facilities (BULK - optimized)
    getAnalysisSummaries: () => apiCall('/analyses/summaries'),

    // Get analysis summary for a single facility (individual - use sparingly)
    getFacilityAnalysisSummary: (facilityId) => apiCall(`/facilities/${facilityId}/analysis-summary`),
};

/**
 * Utility functions
 */
export const utils = {
    // Format error message for display
    formatError: (error) => {
        if (error instanceof ApiError) {
            return error.message;
        }
        return error.message || 'An unexpected error occurred';
    },

    // Check if API is available
    checkApiAvailability: async () => {
        try {
            await systemApi.healthCheck();
            return true;
        } catch {
            return false;
        }
    },

    // Retry API call with exponential backoff
    retryApiCall: async (apiFunction, maxRetries = 3, baseDelay = 1000) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await apiFunction();
            } catch (error) {
                if (attempt === maxRetries) throw error;

                const delay = baseDelay * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }
};

const api = {
    system: systemApi,
    facilities: facilitiesApi,
    analysis: analysisApi,
    results: resultsApi,
};

export default api; 